import type { ConnectionStatus, DataFlow } from '../../catalog/enums';

/**
 * The product an integration connection belongs to. Ported from the Ember
 * `ProductApp` enum (`app/utils/product-app-info.ts`) — the string values are
 * load-bearing (they appear in the connections-table grouping key).
 */
export const ProductApp = {
  journeys: 'journeys',
  dataPipelines: 'pipelines',
} as const;
export type ProductApp = (typeof ProductApp)[keyof typeof ProductApp];

/**
 * A single row in the Connections table. Journeys connections are produced by
 * the view-model parsers; Pipelines connections are normalized from the
 * `PipelinesConnection` wire records. Ported from the Ember
 * `IntegrationConnection` interface plus the `NestableConnection` extension the
 * connections table adds (`children` / `isAutoTrack`).
 */
export interface IntegrationConnection {
  id: string;
  /** Not the integration *type* — the integration id, reused as a grouping key. */
  type: string;
  icon?: string;
  imageSrc?: string;
  name: string;
  dataFlow?: DataFlow | null;
  product: ProductApp;
  href?: string;
  path: string;
  dynamicSegment?: string;
  models?: (string | number)[];
  status?: ConnectionStatus;
  statusMessage?: string;
  createdAt?: number;
  lastActivity?: number;
  subtitle?: string;
  connectedTo?: number[];
  category?: string;
  /** Pipelines-only: 'Source' / 'Destination'. */
  direction?: string;
  /** Pipelines-only: whether this is an auto-track (Track-API) source. */
  isAutoTrack?: boolean;
  /** Pipelines-only: the joined catalog integration's display name. */
  integrationName?: string;
  /** Grouped-parent rows only: the nested child connections. */
  children?: IntegrationConnection[];
}

/* ------------------------------------------------------------------------- */
/* Data sources bag (produced by useConnectionSources, consumed by parsers)   */
/* ------------------------------------------------------------------------- */

/** One SQL-import sync (all timestamps unix seconds). */
export interface SqlImportSyncSource {
  active: boolean;
  /** `undefined` when the sync carries no `created_at` — must not be folded into the group minimum. */
  createdAt: number | undefined;
  lastSyncedAt: number;
  imports: readonly SqlImportRowSource[];
}

/** One SQL-import row/run. `isFailed` / `isPartiallyFailed` are pre-derived. */
export interface SqlImportRowSource {
  createdAt: number;
  endedAt: number;
  isFailed: boolean;
  isPartiallyFailed: boolean;
  error: string;
}

/** One NativeDataOut (amplitude / mixpanel) integration config. */
export interface DataOutSource {
  createdAt: number;
  lastActivityAt: number;
  enabled: boolean;
  events: readonly string[] | null;
}

/** One ad-network audience (adwords / facebook). */
export interface AdAudienceSource {
  id: string;
  name: string;
  state: string;
  adNetworkName: string;
  /** The matching audience-credential's `expired` flag. */
  credentialExpired: boolean;
  createdAt: number;
  lastSynced: number;
  lastError: string;
}

/** One connected/draft/disconnected custom/type/jot form. `errorMessage` is pre-derived from `getFormErrorMessage`. */
export interface FormSource {
  /** Numeric record id (`custom_forms.id`) — the segment the `form_details` route resolves by. */
  id: string;
  name: string;
  formType: string;
  formId: string;
  createdAt: number;
  lastActivityAt: number;
  status: string;
  hasError: boolean;
  errorMessage?: string;
  isScannedForm: boolean;
  isApiForm: boolean;
  isTypeform: boolean;
  isJotform: boolean;
}

/** One reporting webhook. */
export interface ReportingWebhookSource {
  id: string;
  name: string;
  events: readonly string[] | null;
  createdAt: number;
  lastActivityAt: number;
  enabled: boolean;
}

/**
 * Every overview-only data source the connection parsers read, pre-parsed to
 * the shapes the parsers consume (unix-second timestamps, plain booleans,
 * pre-derived flags). Assembled by `useConnectionSources`. A source that failed
 * to load / is not configured resolves to its "absent" default (`null` /
 * `false` / `[]`), never surfacing as an error.
 */
export interface ConnectionSources {
  /** The environment's `enabled_actions` — channel parsers gate on these. */
  enabledActions: readonly string[];
  /** API keys (unix-second timestamps; `lastUsedAt` is 0 for unused keys). */
  apiCredentials: readonly { lastUsedAt: number; createdAt: number }[];
  /** Warehouse-sync status, or null when not configured. `lastActivity` = latest export-file time. */
  warehouseSync: { createdAt: number; lastActivity: number; isPaused: boolean } | null;
  /** SQL imports keyed by database slug (`mysql` / `postgres` / `sqlserver` / `bigquery` / `redshift` / `snowflake`), or null when the fetch failed. */
  sqlImportsByDatabase: Record<string, readonly SqlImportSyncSource[]> | null;
  /** NativeDataOut configs keyed by integration id (`amplitude` / `mixpanel`). */
  dataOut: Record<string, DataOutSource>;
  /** Segment-out (segdotcom) status, or null when not configured. */
  segmentOut: { createdAt: number; lastActivity: number; enabled: boolean } | null;
  /** mParticle status, or null when not configured. */
  mparticle: { createdAt: number; lastActivityAt: number; enabled: boolean } | null;
  /** Ad audiences keyed by network (`adwords` / `facebook`), or null when the fetch failed. */
  adAudiences: Record<string, readonly AdAudienceSource[]> | null;
  /** Facebook Lead Ads status. */
  facebookLeadAds: {
    enabled: boolean;
    credentialCreatedAt: number | undefined;
    connectedFormsLastActivity: readonly number[];
  };
  /** Reporting webhooks. */
  reportingWebhooks: readonly ReportingWebhookSource[];
  /** Connected/draft/disconnected custom + type + jot forms. */
  forms: readonly FormSource[];
  /** In-app channel: creation timestamp, if configured. */
  inAppCreated: number | undefined;
  /** Push channel: configured platform keys + earliest credential creation. */
  push: { platforms: readonly string[]; createdAt: number | undefined };
  /**
   * Slack channel — mirrors the Ember inverted guard: a Slack connection shows
   * only when the credential fetch resolved to exactly `null`. `true` here means
   * the fetch resolved to null.
   */
  slackCredentialIsNull: boolean;
  /** Twilio channel — same inverted-guard semantics as Slack. */
  twilioCredentialIsNull: boolean;
  /** Email channel: whether the email setup flow is complete. */
  emailSetupComplete: boolean;
  /** URL-params channel. */
  urlParams: { enabled: boolean; created: number | undefined };
}

/** Non-data config the parsers/build read (feature flags + workspace). */
export interface ConnectionsConfig {
  workspaceId: string;
  /** `cdp_track_enabled` OR `cdp_passthrough_enabled`. */
  areAutoTrackSourcesEnabled: boolean;
}
